import { useEffect, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { useTheme, globalPart, NAVBAR_VARIANTS, SIDEBAR_VARIANTS, ACCENT_VARIANTS, BRAND_VARIANTS } from "@/context/ThemeContext";
import { useStore } from "@/context/StoreContext";
import { Page, BrandLogo, resizeImage, PrioBadge } from "@/components/ui-z";
import { PRIORIDADES, DEFAULT_SLA } from "@/lib/constants";

// Prazos (SLA) por prioridade — salvos na API (PUT /api/settings/sla) e usados também nos alertas do backend
const SlaCard = () => {
  const { sla, saveSla } = useStore();
  const toForm = (c) => ({ hours: { ...DEFAULT_SLA.hours, ...c.hours }, warn: Math.round((c.warn_ratio || DEFAULT_SLA.warn_ratio) * 100) });
  const [f, setF] = useState(() => toForm(sla));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(toForm(sla)); }, [sla]);

  const setHours = (k, v) => setF((x) => ({ ...x, hours: { ...x.hours, [k]: v } }));
  const save = async () => {
    const hours = Object.fromEntries(PRIORIDADES.map((p) => [p.key, Number(f.hours[p.key])]));
    if (Object.values(hours).some((h) => !Number.isInteger(h) || h < 1 || h > 8760)) return toast.error("Informe horas inteiras entre 1 e 8760");
    const warn = Number(f.warn);
    if (!(warn >= 5 && warn <= 90)) return toast.error("O alerta deve ficar entre 5% e 90% do prazo");
    setSaving(true);
    const ok = await saveSla({ hours, warn_ratio: warn / 100 });
    setSaving(false);
    if (ok) toast.success("Prazos de SLA salvos");
  };

  return (
    <div className="card card-warning card-outline" data-testid="sla-settings-card">
      <div className="card-header">
        <h3 className="card-title"><i className="far fa-clock mr-1" />Prazos (SLA) por prioridade</h3>
        <div className="card-tools">
          <button type="button" className="btn btn-tool" title="Valores padrão" data-testid="sla-defaults-btn" onClick={() => setF(toForm(DEFAULT_SLA))}><i className="fas fa-undo" /></button>
        </div>
      </div>
      <div className="card-body">
        <div className="row">
          {PRIORIDADES.map((p) => (
            <div key={p.key} className="col-md-2 col-6 form-group">
              <label htmlFor={`sla-${p.key}`}><PrioBadge prio={p.key} /></label>
              <div className="input-group">
                <input id={`sla-${p.key}`} data-testid={`sla-hours-${p.key}`} type="number" min={1} max={8760} step={1} className="form-control" value={f.hours[p.key]} onChange={(e) => setHours(p.key, e.target.value)} />
                <div className="input-group-append"><span className="input-group-text">h</span></div>
              </div>
            </div>
          ))}
          <div className="col-md-4 form-group">
            <label htmlFor="sla-warn">Alertar quando faltar</label>
            <div className="input-group">
              <input id="sla-warn" data-testid="sla-warn-input" type="number" min={5} max={90} step={1} className="form-control" value={f.warn} onChange={(e) => setF((x) => ({ ...x, warn: e.target.value }))} />
              <div className="input-group-append"><span className="input-group-text">% do prazo</span></div>
            </div>
          </div>
        </div>
        <small className="text-muted">O prazo conta da abertura até a resolução. Chamados abertos passam a "perto de estourar" quando resta o percentual acima.</small>
      </div>
      <div className="card-footer">
        <button type="button" data-testid="sla-save-btn" className="btn btn-primary" disabled={saving} onClick={save}>{saving && <i className="fas fa-spinner fa-spin mr-1" />}Salvar prazos</button>
      </div>
    </div>
  );
};

// Produtos que o solicitante escolhe na abertura do chamado (portal e Slack), na ordem desta lista.
// Sem exclusão: inativar tira o produto da abertura, mas ele continua nos chamados antigos
const ProdutosCard = () => {
  const { produtos, createProduto, updateProduto, reorderProdutos } = useStore();
  const [novo, setNovo] = useState("");
  const [editando, setEditando] = useState(null); // { id, nome }

  const adicionar = async (e) => {
    e.preventDefault();
    const nome = novo.trim();
    if (!nome) return toast.error("Informe o nome do produto");
    if (await createProduto(nome)) { setNovo(""); toast.success(`Produto "${nome}" cadastrado`); }
  };
  const renomear = async () => {
    const nome = editando.nome.trim();
    if (!nome) return toast.error("Informe o nome do produto");
    if (await updateProduto(editando.id, { nome })) { setEditando(null); toast.success(`Produto renomeado para "${nome}"`); }
  };
  const alternarAtivo = async (p, ativo) => {
    if (await updateProduto(p.id, { ativo })) toast.success(`Produto "${p.nome}" ${ativo ? "ativado" : "inativado"}`);
  };
  const mover = async (i, delta) => {
    const ids = produtos.map((p) => p.id);
    [ids[i], ids[i + delta]] = [ids[i + delta], ids[i]];
    if (await reorderProdutos(ids)) toast.success(`Ordem dos produtos salva ("${produtos[i].nome}" ${delta < 0 ? "subiu" : "desceu"})`);
  };

  return (
    <div className="card card-info card-outline" data-testid="produtos-card">
      <div className="card-header">
        <h3 className="card-title"><i className="fas fa-cubes mr-1" />Produtos</h3>
      </div>
      <div className="card-body p-0">
        <table className="table table-sm table-hover mb-0">
          <thead><tr><th style={{ width: 60 }}>#</th><th>Produto</th><th style={{ width: 110 }}>Ativo</th><th style={{ width: 150 }} /></tr></thead>
          <tbody>
            {produtos.map((p, i) => (
              <tr key={p.id} className={p.ativo ? "" : "text-muted"} data-testid={`produto-row-${p.id}`}>
                <td className="align-middle">{i + 1}</td>
                <td className="align-middle">
                  {editando?.id === p.id ? (
                    <div className="input-group input-group-sm">
                      <input autoFocus className="form-control" maxLength={100} value={editando.nome} data-testid="produto-edit-input"
                        onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") renomear(); if (e.key === "Escape") setEditando(null); }} />
                      <div className="input-group-append">
                        <button type="button" className="btn btn-primary" title="Salvar" data-testid="produto-edit-save" onClick={renomear}><i className="fas fa-check" /></button>
                        <button type="button" className="btn btn-default" title="Cancelar" onClick={() => setEditando(null)}><i className="fas fa-times" /></button>
                      </div>
                    </div>
                  ) : p.nome}
                </td>
                <td className="align-middle">
                  <div className="custom-control custom-switch">
                    <input id={`produto-ativo-${p.id}`} type="checkbox" className="custom-control-input" checked={p.ativo} data-testid={`produto-ativo-${p.id}`}
                      onChange={(e) => alternarAtivo(p, e.target.checked)} />
                    <label className="custom-control-label font-weight-normal" htmlFor={`produto-ativo-${p.id}`}>{p.ativo ? "Sim" : "Não"}</label>
                  </div>
                </td>
                <td className="align-middle text-right text-nowrap">
                  <button type="button" className="btn btn-tool" title="Subir" disabled={i === 0} onClick={() => mover(i, -1)}><i className="fas fa-arrow-up" /></button>
                  <button type="button" className="btn btn-tool" title="Descer" disabled={i === produtos.length - 1} onClick={() => mover(i, 1)}><i className="fas fa-arrow-down" /></button>
                  <button type="button" className="btn btn-tool" title="Renomear" data-testid={`produto-edit-${p.id}`} onClick={() => setEditando({ id: p.id, nome: p.nome })}><i className="fas fa-pen" /></button>
                </td>
              </tr>
            ))}
            {!produtos.length && <tr><td colSpan={4} className="text-center text-muted py-3">Nenhum produto cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="card-footer">
        <form className="input-group" onSubmit={adicionar}>
          <input className="form-control" placeholder="Novo produto" maxLength={100} value={novo} data-testid="produto-novo-input" onChange={(e) => setNovo(e.target.value)} />
          <div className="input-group-append"><button type="submit" className="btn btn-primary" data-testid="produto-novo-btn"><i className="fas fa-plus mr-1" />Adicionar</button></div>
        </form>
        <small className="text-muted">Quem abre um chamado escolhe o produto nesta ordem. Produto inativo não aparece na abertura, mas continua nos chamados antigos.</small>
      </div>
    </div>
  );
};

// Mesmas opções do painel "Customize AdminLTE" (dist/js/demo.js)
const CHECKS = [
  ["darkMode", "Dark Mode"],
  ["navbarFixed", "Header fixo"],
  ["footerFixed", "Footer fixo"],
  ["sidebarCollapsed", "Sidebar recolhida"],
  ["textSm", "Texto pequeno (text-sm)"],
  ["navFlat", "Sidebar Nav Flat Style"],
  ["navLegacy", "Sidebar Nav Legacy Style"],
  ["navCompact", "Sidebar Nav Compact"],
  ["navChildIndent", "Sidebar Nav Child Indent"],
];

const Select = ({ id, label, value, options, onChange, empty }) => (
  <div className="form-group">
    <label htmlFor={id}>{label}</label>
    <select id={id} data-testid={`skin-${id}`} className="custom-select" value={value} onChange={(e) => onChange(e.target.value)}>
      {empty && <option value="">{empty}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

// Salva o tema no banco (vale para todos) logo depois de cada alteração. Ao abrir a tela não salva nada,
// a não ser que o banco ainda não tenha tema: aí publica o que está aqui (a personalização feita antes, no navegador).
// O resultado de cada gravação aparece em toast
const useSaveTheme = () => {
  const { skin, remote, saveGlobal } = useTheme();
  const first = useRef(true);
  const skinRef = useRef(skin);
  skinRef.current = skin;
  const assinatura = JSON.stringify(globalPart(skin));

  useEffect(() => {
    if (!remote.loaded || remote.error) return undefined;
    if (first.current) {
      first.current = false;
      if (!remote.empty) return undefined;
    }
    const t = setTimeout(() => {
      saveGlobal(skinRef.current)
        .then(() => toast.success("Tema salvo para todos os usuários"))
        .catch((err) => toast.error(`Tema não foi salvo: ${err.message}`));
    }, 700);
    return () => clearTimeout(t);
  }, [assinatura, remote.loaded, remote.empty, remote.error, saveGlobal]);

  // API sem o tema: avisa uma vez ao abrir a tela
  useEffect(() => {
    if (remote.error) toast.error("Não foi possível carregar o tema da API; as alterações não serão salvas.");
  }, [remote.error]);
};

export default function Customizacao() {
  const { skin, update, reset } = useTheme();
  useSaveTheme();
  return (
    <Page title="Customização" crumbs={[["Gestão"], ["Tema"]]}>
      <div className="callout callout-info py-2" data-testid="theme-save-status">
        <i className="fas fa-users mr-2 text-info" />Marca, logo, layout e cores são os mesmos para todos e são salvos automaticamente. O modo noturno continua sendo escolha de cada pessoa.
      </div>
      <div className="card card-primary card-outline">
        <div className="card-header"><h3 className="card-title"><i className="fas fa-copyright mr-1" />Marca (nome e logo do menu lateral)</h3></div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-5 form-group">
              <label htmlFor="brand-name">Nome</label>
              <input id="brand-name" data-testid="brand-name-input" className="form-control" maxLength={40} value={skin.brandName} onChange={(e) => update({ brandName: e.target.value })} />
            </div>
            <div className="col-md-5 form-group">
              <label htmlFor="brand-logo">Logo</label>
              <div className="custom-file">
                <input id="brand-logo" data-testid="brand-logo-input" type="file" accept="image/*" className="custom-file-input" onChange={async (e) => {
                  const f = e.target.files[0];
                  e.target.value = "";
                  if (!f) return;
                  try { update({ brandLogo: await resizeImage(f) }); toast.success("Logo atualizada"); } catch (err) { toast.error(err.message); }
                }} />
                <label className="custom-file-label" htmlFor="brand-logo">Escolher imagem</label>
              </div>
              <small className="form-text text-muted">PNG, JPG ou SVG. A imagem é reduzida para 128px.</small>
              <div className="custom-control custom-checkbox mt-2">
                <input id="brand-circle" data-testid="brand-circle-checkbox" type="checkbox" className="custom-control-input" checked={skin.brandLogoCircle} onChange={(e) => update({ brandLogoCircle: e.target.checked })} />
                <label className="custom-control-label font-weight-normal" htmlFor="brand-circle">Logo arredondada</label>
              </div>
              <div className="custom-control custom-switch mt-2">
                <input id="brand-show-title" data-testid="brand-show-title-switch" type="checkbox" className="custom-control-input" checked={skin.brandShowTitle} onChange={(e) => update({ brandShowTitle: e.target.checked })} />
                <label className="custom-control-label font-weight-normal" htmlFor="brand-show-title">Mostrar título do sidebar</label>
              </div>
            </div>
            <div className="col-md-2 form-group">
              <label>Prévia</label>
              <div className={`${skin.sidebarVariant} rounded elevation-1 overflow-hidden`} data-testid="brand-preview">
                <span className={`brand-link border-bottom-0 ${skin.brandVariant} ${skin.brandShowTitle ? "" : "brand-logo-only"}`}><BrandLogo skin={skin} />{skin.brandShowTitle && <span className="brand-text font-weight-light">{skin.brandName}</span>}</span>
              </div>
              {skin.brandLogo && <button type="button" data-testid="brand-logo-remove" className="btn btn-link btn-sm px-0" onClick={() => update({ brandLogo: "" })}><i className="fas fa-times mr-1" />Remover logo</button>}
            </div>
          </div>
        </div>
      </div>

      <SlaCard />

      <ProdutosCard />

      <div className="row">
        <div className="col-lg-6">
          <div className="card card-primary card-outline">
            <div className="card-header">
              <h3 className="card-title">Customize AdminLTE</h3>
              <div className="card-tools">
                <button type="button" data-testid="theme-reset-btn" className="btn btn-tool" title="Restaurar padrão" onClick={reset}><i className="fas fa-undo" /></button>
              </div>
            </div>
            <div className="card-body">
              {CHECKS.map(([k, l]) => (
                <div key={k} className="custom-control custom-checkbox mb-2">
                  <input id={`skin-${k}`} data-testid={`skin-${k}`} type="checkbox" className="custom-control-input" checked={skin[k]} onChange={(e) => update({ [k]: e.target.checked })} />
                  <label className="custom-control-label font-weight-normal" htmlFor={`skin-${k}`}>{l}</label>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card card-primary card-outline">
            <div className="card-header"><h3 className="card-title">Variantes de cor</h3></div>
            <div className="card-body">
              <Select id="navbar" label="Navbar Variants" value={skin.navbarVariant} options={NAVBAR_VARIANTS} onChange={(v) => update({ navbarVariant: v })} />
              <Select id="accent" label="Accent Color Variants" value={skin.accent} options={ACCENT_VARIANTS} empty="None Selected" onChange={(v) => update({ accent: v })} />
              <Select id="sidebar" label="Sidebar Variants" value={skin.sidebarVariant} options={SIDEBAR_VARIANTS} onChange={(v) => update({ sidebarVariant: v })} />
              <Select id="brand" label="Brand Logo Variants" value={skin.brandVariant} options={BRAND_VARIANTS} empty="None Selected" onChange={(v) => update({ brandVariant: v })} />
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}
